import {
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { T } from 'react-translator-component';
import styled from 'styled-components';

import { AlertsCardInnerContent } from './AlertsCardInnerContent/AlertsCardInnerContent';
import { combiningObjects } from './listFormater';

import { INotifications } from '../../../types/responses/Notifications';
import {
  normalOrder,
  twoOrMoreWordsOrder,
} from '../../../utils/columnHeaderOrder';
import { alertsListFilter, setIconActive } from '../../../utils/list';
import { HeaderIcon } from '../../molecules/HeaderIcon/HeaderIcon';
import { Search } from '../../molecules/Search';
import CardListConstructor from '../CardListConstructor/CardListConstructor';
import { closeAllCards } from '../CardListConstructor/helpers';

interface IAlertsTable {
  notifications: INotifications[];
}

const AlertsTable = ({ notifications }: IAlertsTable) => {
  const [searchInput, setsearchInput] = useState('');

  const HEADER = useMemo(
    () => [
      {
        text: T('Station'),
        sideIcon: <HeaderIcon />,
        action: (e: MouseEvent<HTMLDivElement>) =>
          normalOrder(
            'chargeBoxName',
            notifications,
            (newList) => {
              setIconActive(e);
              closeAllCards();
              paginate();
              setdata(combiningObjects(newList, HEADER));
              setpaginationData(newList);
            },
            e,
          ),
        size: '20%',
        // the size is to be set in (flex-basis). So the total number of sizes of the header
        // must be equal to 100%
        dropdown: <></>,
      },
      {
        text: T('State'),
        sideIcon: <HeaderIcon />,
        action: (e: MouseEvent<HTMLDivElement>) =>
          normalOrder(
            'addressState',
            alertsListFilter(searchInput, notifications),
            (newList) => {
              setIconActive(e);
              closeAllCards();
              paginate();
              setdata(combiningObjects(newList, HEADER));
              setpaginationData(newList);
            },
            e,
          ),
        size: '12%',
        dropdown: <></>,
      },
      {
        text: T('City'),
        sideIcon: <HeaderIcon />,
        action: (e: MouseEvent<HTMLDivElement>) =>
          twoOrMoreWordsOrder(
            'addressCity',
            alertsListFilter(searchInput, notifications),
            (newList) => {
              setIconActive(e);
              closeAllCards();
              paginate();
              setdata(combiningObjects(newList, HEADER));
              setpaginationData(newList);
            },
            e,
          ),
        size: '13%',
        dropdown: <></>,
      },
      {
        text: T('Place'),
        sideIcon: <HeaderIcon />,
        action: (e: MouseEvent<HTMLDivElement>) =>
          normalOrder(
            'addressStreet',
            alertsListFilter(searchInput, notifications),
            (newList) => {
              setIconActive(e);
              closeAllCards();
              paginate();
              setdata(combiningObjects(newList, HEADER));
              setpaginationData(newList);
            },
            e,
          ),
        size: '30%',
        dropdown: <></>,
      },
      {
        text: T('Date'),
        sideIcon: <HeaderIcon />,
        action: (e: MouseEvent<HTMLDivElement>) =>
          normalOrder(
            'notificationTimestampDT',
            alertsListFilter(searchInput, notifications),
            (newList) => {
              setIconActive(e);
              closeAllCards();
              paginate();
              setdata(combiningObjects(newList, HEADER));
              setpaginationData(newList);
            },
            e,
          ),
        size: '15%',
        dropdown: <></>,
      },
      {
        text: T('Event'),
        sideIcon: null,
        action: () => {},
        size: '10%',
        dropdown: <></>,
      },
    ],
    [notifications, searchInput],
  );

  const [paginationData, setpaginationData] = useState<any>([]);
  const [data, setdata] = useState<any>(
    combiningObjects(notifications, HEADER),
  );

  const childRef: any = useRef();
  const paginate = () => childRef?.current?.paginate();

  useEffect(() => {
    setdata(combiningObjects(notifications, HEADER));
    setpaginationData(notifications);
  }, [notifications, HEADER]);

  type FilterNotifications = (e: any) => void;
  const filterNotifications: FilterNotifications = useCallback((value) => {
    setsearchInput(value);
  }, []);

  const clearSearch = useCallback(() => {
    setsearchInput('');
    setdata(combiningObjects(notifications, HEADER));
  }, [HEADER, notifications]);

  useEffect(() => {
    setpaginationData(alertsListFilter(searchInput, notifications));
    setdata(
      combiningObjects(alertsListFilter(searchInput, notifications), HEADER),
    );
  }, [notifications, searchInput, HEADER]);

  useEffect(() => {
    paginate();
  }, [paginationData]);

  return (
    <Div>
      <div className='tools_bar'>
        <h1 className='page_title'>{T('Alerts')}</h1>
        <Search
          className='search_input'
          placeholder={T('Search By Name or ChargeboxId (below the name)')}
          action={filterNotifications}
          closeBtn={clearSearch}
          value={searchInput}
        />
      </div>
      {
        <ListWrapper>
          <CardListConstructor
            childRef={childRef}
            data={data}
            InnerComponent={AlertsCardInnerContent}
            paginationCallback={(newList) =>
              setdata(combiningObjects(newList, HEADER))
            }
            pureData={alertsListFilter(searchInput, paginationData)}
          />
        </ListWrapper>
      }
    </Div>
  );
};

export default AlertsTable;

const ListWrapper = styled.div`
  padding-left: 24px;
  padding-right: 24px;
`;

const Div = styled.div`
  width: 100%;

  .tools_bar {
    display: flex;
    flex-direction: row;
    width: 100%;
    align-items: center;
    justify-content: flex-end;

    .page_title {
      flex: auto;
      margin: 20px 0 0 2%;
      font-size: 21px;
      font-weight: normal;
      font-family: 'Roboto';
      color: #777;
    }
  }

  .search_input {
    width: 40%;
    margin: 23px 2% 0 0;
  }

  .search_input input {
    box-shadow: 0 0 10px 1px rgba(0, 0, 0, 0.1);
    padding: 0 70px 0 30px;
    width: 100%;
    border-radius: 8px;
  }

  .card_in_list.gabrr_expanded {
    height: 200px;
  }
`;
